// Sprint 12 (D-008) — binary Working / Idle input model.
//
// "Input is always writable." The chat composer is disabled only in the
// genuinely unrecoverable case (error + no claudeSessionId). Every other
// state either writes immediately or triggers a transparent respawn.
//
// Sprint lazy-spawn-recovery (§2 D12) — the frontend composer gate is
// REMOVED. `error + no claudeSessionId` used to mean "permanently
// un-continuable, offer Fork" (D-008's `UnrecoverableBanner`, deleted in
// Composer.tsx this sprint); after §2 D7 the backend makes every pre-init
// death recoverable on the next send, so that predicate can never fire
// for a case this sprint hasn't already proven recoverable. The one case
// it CANNOT match (D-008's genuine GC'd-session case — which by
// definition HAS a claudeSessionId) has no detector today and is filed as
// D013-EC-279 rather than silently inherited by this gate.
//
// `computeInputBlocked` stays exported (rather than deleted outright) so
// the full status × session matrix remains machine-verified — it now
// always returns `'none'`.

import type { Agent, AgentStatus } from '../types';
import { t } from '../i18n';

export type InputBlockedReason = 'none';

export function computeInputBlocked(
  _agent: Pick<Agent, 'status' | 'claudeSessionId'>,
): InputBlockedReason {
  return 'none';
}

export function isInputBlocked(agent: Pick<Agent, 'status' | 'claudeSessionId'>): boolean {
  return computeInputBlocked(agent) !== 'none';
}

export interface PlaceholderContext {
  status: AgentStatus;
  hasSession: boolean;
  isDisconnected?: boolean;
  isArchived?: boolean;
  hasPermissionPending?: boolean;
  // Sprint working-signal-ssot — server-derived task-scoped signal.
  // Optional; when absent the status-only mapping applies.
  working?: boolean;
}

export function placeholderForStatus(
  statusOrCtx: AgentStatus | PlaceholderContext,
  hasSession?: boolean,
): string {
  if (typeof statusOrCtx === 'string') {
    return placeholderForContext({ status: statusOrCtx, hasSession: hasSession ?? false });
  }
  return placeholderForContext(statusOrCtx);
}

// Sprint 12 — collapsed to binary: Working = "Add a follow-up…",
// Idle = "Send a message…". Priority overrides preserved.
function placeholderForContext(ctx: PlaceholderContext): string {
  if (ctx.isDisconnected) return t('composer.placeholder.disconnected');
  if (ctx.isArchived) return t('composer.placeholder.archived');
  if (ctx.hasPermissionPending) return t('composer.placeholder.permissionPending');

  if (ctx.working ?? ctx.status === 'running') {
    return t('composer.placeholder.running');
  }

  return t('composer.placeholder.idle');
}

/**
 * Kept for API compatibility. Sprint 12 removes all visible hints —
 * the composer being enabled is the clearest signal.
 */
export function hintForStatus(_status: AgentStatus): string | null {
  return null;
}
